import * as React from 'react'

const STROKE_WIDTH = 3.5 // Set the stroke width here so it can be used in calculations

interface CircleProgressProps {
  size?: string
  progress: number
  className?: string
  baseColor?: string
  progressColor?: string
  isStatic?: boolean
  isStartFromZero?: boolean
}
export const CircleProgress: React.FC<CircleProgressProps> = React.forwardRef<SVGSVGElement, CircleProgressProps>(
  (
    {
      className,
      progress = 0,
      baseColor = '#E7E9EC',
      progressColor = '#4A7FE5',
      size = '100%', // Default to 100% to fill the container
      isStatic = false,
      isStartFromZero = false,
    },
    ref
  ) => {
    // Define the size of the SVG drawing area
    const viewBoxSize = 100
    // Calculate the radius of the circle based on the viewBox size and stroke width
    const radius = viewBoxSize / 2 - STROKE_WIDTH * 2
    // Calculate the circumference of the circle
    const circumference = 2 * Math.PI * radius
    const targetOffset = circumference - (progress / 100) * circumference
    const [animatedOffset, setAnimatedOffset] = React.useState<number | null>(() => (isStatic ? targetOffset : null))

    React.useEffect(() => {
      if (isStatic) {
        setAnimatedOffset(targetOffset)
        return
      }
      if (isNaN(targetOffset)) {
        console.error('[circle-progress] Bad values passed into circle_progress: %o', { circumference, progress })
        return
      }

      // Animate using requestAnimationFrame
      const duration = 1000
      const startTime = performance.now()
      const startValue = isStartFromZero ? circumference : (animatedOffset ?? circumference)

      const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

      let animationId: number
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progressRatio = Math.min(elapsed / duration, 1)
        const easedProgress = easeInOut(progressRatio)
        const currentValue = startValue + (targetOffset - startValue) * easedProgress

        setAnimatedOffset(currentValue)

        if (progressRatio < 1) {
          animationId = requestAnimationFrame(animate)
        }
      }

      animationId = requestAnimationFrame(animate)
      return () => cancelAnimationFrame(animationId)
    }, [isStatic, progress, circumference, isStartFromZero, targetOffset])

    if (isNaN(progress)) {
      return null
    }

    if (isNaN(targetOffset)) {
      console.error('[circle-progress] Bad values passed into circle_progress: %o', { circumference, progress })
    }

    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        ref={ref}
      >
        <circle cx={viewBoxSize / 2} cy={viewBoxSize / 2} r={radius} fill="#FFFFFF" />
        <circle cx={viewBoxSize / 2} cy={viewBoxSize / 2} r={radius} stroke={baseColor} strokeWidth={'7%'} />
        <circle
          cx={viewBoxSize / 2}
          cy={viewBoxSize / 2}
          r={radius}
          stroke={progressColor}
          strokeWidth={'7%'}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={animatedOffset ?? circumference}
          transform={`rotate(-90 ${viewBoxSize / 2} ${viewBoxSize / 2})`}
        />
      </svg>
    )
  }
)

CircleProgress.displayName = 'CircleProgress'
